import * as Popover from '@radix-ui/react-popover';
import type { PartnerOrganization } from '@reach/shared-types/workos-auth';
import { useQuery } from '@tanstack/react-query';
import { Check, ChevronDown, Loader2, Search, Shield } from 'lucide-react';
import React, { useMemo, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { partnerApi, type OrganizationsResponse, type Partner } from '../lib/api';
import { forgetActiveGeneration } from '../pages/feature-setup/automations-tab/reputation-defaults-menu/activeGenerationStorage';
import { clearAiSegmentDraft } from '../pages/feature-setup/segments-tab/describe-segment-modal/types';

interface OrgSwitcherProps {
  partner: Partner;
}

export const OrgSwitcher: React.FC<OrgSwitcherProps> = ({ partner }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [switchingToOrgId, setSwitchingToOrgId] = useState<string | null>(null);
  const [focusedIndex, setFocusedIndex] = useState(0);
  const { user, organization } = useAuth();

  const { data: organizationsResponse, isLoading } = useQuery<OrganizationsResponse>({
    queryKey: ['organizations'],
    queryFn: () => partnerApi.getOrganizations({ limit: 100 }),
    enabled: !!user,
  });

  const organizations = useMemo(() => {
    const data = organizationsResponse?.data as { results: PartnerOrganization[] } | undefined;
    return data?.results || [];
  }, [organizationsResponse]);
  const showSwitcher = organizations.length > 1;

  const filteredOrgs = useMemo(() => {
    if (!searchQuery.trim()) return organizations;
    const query = searchQuery.toLowerCase();
    return organizations.filter(
      org => org.name.toLowerCase().includes(query) || org.domain?.toLowerCase().includes(query),
    );
  }, [organizations, searchQuery]);

  const handleSwitch = async (orgId: string) => {
    if (orgId === organization?.id) {
      setIsOpen(false);
      return;
    }

    // Drafts are scoped to the organization that generated them; the reauth
    // redirect below keeps sessionStorage, so drop it before leaving.
    clearAiSegmentDraft();
    forgetActiveGeneration();

    setSwitchingToOrgId(orgId);
    try {
      const data = await partnerApi.switchOrganization(orgId);
      if (data.data.requiresReauth) {
        window.location.href = data.data.authorizationUrl;
      }
    } catch (error) {
      console.error('Error switching organization:', error);
      setSwitchingToOrgId(null);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setFocusedIndex(i => Math.min(i + 1, filteredOrgs.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setFocusedIndex(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const org = filteredOrgs[focusedIndex];
      if (org) handleSwitch(org.id);
    }
  };

  return (
    <div className="border-b border-slate-700">
      <Popover.Root
        open={isOpen}
        onOpenChange={open => {
          setIsOpen(open);
          if (!open) {
            setSearchQuery('');
            setFocusedIndex(0);
          }
        }}>
        <Popover.Trigger asChild>
          <button
            disabled={!showSwitcher}
            className={`w-full px-4 py-5 ${
              showSwitcher ? 'hover:bg-slate-700 cursor-pointer' : ''
            } transition-colors`}>
            <div className="flex items-center space-x-3">
              <OrgLogo logoUrl={partner.logoUrl} name={partner.name} size="lg" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center space-x-2">
                  <p className="text-sm font-medium text-white truncate font-nav">
                    {organization?.name || partner.name}
                  </p>
                  {showSwitcher && (
                    <ChevronDown
                      className={`h-4 w-4 text-slate-400 flex-shrink-0 transition-transform ${
                        isOpen ? 'rotate-180' : ''
                      }`}
                    />
                  )}
                </div>
              </div>
            </div>
          </button>
        </Popover.Trigger>

        <Popover.Portal>
          <Popover.Content
            side="right"
            align="start"
            sideOffset={4}
            onKeyDown={handleKeyDown}
            className="z-50 w-72 bg-slate-900 border border-slate-700 rounded-lg shadow-xl">
            {/* Search input */}
            <div className="p-3 border-b border-slate-700">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-500" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={e => {
                    setSearchQuery(e.target.value);
                    setFocusedIndex(0);
                  }}
                  placeholder="Search organizations..."
                  autoFocus
                  className="w-full bg-slate-800 text-sm text-white placeholder-slate-500 rounded-md pl-8 pr-3 py-1.5 border border-slate-700 focus:outline-none focus:border-slate-500"
                />
              </div>
            </div>

            {/* Org list */}
            {isLoading ? (
              <div className="px-4 py-3 flex items-center space-x-2 text-sm text-slate-400">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Loading...</span>
              </div>
            ) : filteredOrgs.length === 0 ? (
              <div className="px-4 py-3 text-sm text-slate-500">No organizations found</div>
            ) : (
              <div className="max-h-72 overflow-y-auto custom-scrollbar hover-scrollbar">
                {filteredOrgs.map((org, index) => {
                  const isActive = org.id === organization?.id;
                  const isSwitching = switchingToOrgId === org.id;
                  const isFocused = index === focusedIndex;

                  return (
                    <button
                      key={org.id}
                      onClick={() => handleSwitch(org.id)}
                      onMouseEnter={() => setFocusedIndex(index)}
                      disabled={isSwitching}
                      className={`w-full text-left px-3 py-2.5 flex items-center justify-between transition-colors ${
                        isFocused ? 'bg-slate-800' : ''
                      } ${isActive && !isFocused ? 'bg-slate-800/50' : ''} ${
                        isSwitching ? 'opacity-50 cursor-not-allowed' : ''
                      }`}>
                      <div className="flex items-center space-x-3 flex-1 min-w-0">
                        <OrgLogo logoUrl={org.logoUrl} name={org.name} size="sm" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-white truncate">{org.name}</p>
                          {org.domain && <p className="text-xs text-slate-500 truncate">{org.domain}</p>}
                        </div>
                      </div>
                      {isSwitching ? (
                        <Loader2 className="w-4 h-4 animate-spin text-slate-400 flex-shrink-0" />
                      ) : isActive ? (
                        <Check className="w-4 h-4 text-white flex-shrink-0" />
                      ) : null}
                    </button>
                  );
                })}
              </div>
            )}
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
    </div>
  );
};

function OrgLogo({ logoUrl, name, size }: { logoUrl?: string | null; name: string; size: 'sm' | 'lg' }) {
  const sizeClass = size === 'lg' ? 'h-10 w-10' : 'h-6 w-6';
  const iconSize = size === 'lg' ? 'h-5 w-5' : 'h-3 w-3';

  if (logoUrl) {
    return (
      <div
        className={`${sizeClass} rounded-lg flex items-center justify-center overflow-hidden flex-shrink-0 bg-white`}>
        <img src={logoUrl} alt={`${name} logo`} className="h-full w-full object-contain" />
      </div>
    );
  }

  return (
    <div
      className={`${sizeClass} rounded-lg flex items-center justify-center overflow-hidden flex-shrink-0 bg-white`}>
      <Shield className={`${iconSize} text-slate-300`} />
    </div>
  );
}
